/**
 * What the incoming video is actually doing, read off `getStats()`.
 *
 * "Connected but the picture is empty" has three different causes that look the same
 * on screen: no packets arrive (the path is open but the camera sends nothing),
 * packets arrive but no frame is assembled, or frames are assembled but the decoder
 * rejects them — the last is what a phone's hardware decoder does with an H.264
 * profile or size a desktop decodes in software. The counters below tell them apart,
 * so the player shows them instead of leaving a blank rectangle to be guessed at.
 *
 * Kept free of React so it can be checked without a renderer.
 */

export type VideoStats = {
  /** `H264`, `VP8`… — the mime subtype; null when the browser did not link a codec. */
  codec: string | null;
  /** `profile-level-id` for H.264 (e.g. `640033`), null otherwise. */
  profile: string | null;
  width: number | null;
  height: number | null;
  fps: number | null;
  packets: number;
  /** Payload bytes so far — the bitrate is its rate of change, see `LiveStats`. */
  bytes: number;
  framesReceived: number;
  framesDecoded: number;
  framesDropped: number;
  decoder: string | null;
};

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/** The video `inbound-rtp` entry, summarised; null when there is none yet. */
export function summarizeVideoStats(entries: Record<string, unknown>[]): VideoStats | null {
  const rtp = entries.find(
    s => s.type === 'inbound-rtp' && (s.kind === 'video' || s.mediaType === 'video'),
  );
  if (!rtp) {
    return null;
  }
  const codecEntry = entries.find(s => s.type === 'codec' && s.id === rtp.codecId);
  const mime = str(codecEntry?.mimeType);
  const fmtp = str(codecEntry?.sdpFmtpLine);
  const profile = fmtp?.match(/profile-level-id=([0-9a-fA-F]{6})/)?.[1] ?? null;
  return {
    codec: mime ? mime.replace(/^video\//i, '') : null,
    profile,
    width: num(rtp.frameWidth),
    height: num(rtp.frameHeight),
    fps: num(rtp.framesPerSecond),
    packets: num(rtp.packetsReceived) ?? 0,
    bytes: num(rtp.bytesReceived) ?? 0,
    framesReceived: num(rtp.framesReceived) ?? 0,
    framesDecoded: num(rtp.framesDecoded) ?? 0,
    framesDropped: num(rtp.framesDropped) ?? 0,
    decoder: str(rtp.decoderImplementation),
  };
}

/** One line for the diagnostics caption, e.g.
 *  `H264 640033 · 1920×1080 · 25 fps · rtp 812 · 40/38/2 · ExternalDecoder` — the
 *  three numbers are frames received / decoded / dropped. */
export function formatVideoStats(s: VideoStats, fpsUnit: string): string {
  const parts: string[] = [];
  parts.push([s.codec ?? '?', s.profile].filter(Boolean).join(' '));
  if (s.width && s.height) {
    parts.push(`${s.width}×${s.height}`);
  }
  if (s.fps !== null) {
    parts.push(`${Math.round(s.fps)} ${fpsUnit}`);
  }
  parts.push(`rtp ${s.packets}`);
  parts.push(`${s.framesReceived}/${s.framesDecoded}/${s.framesDropped}`);
  if (s.decoder) {
    parts.push(s.decoder);
  }
  return parts.join(' · ');
}
